'use client'

import { createContext, useContext } from 'react'

import type { AuthClient } from '@package/auth/client'

type AuthClientContextValue = AuthClient | null

const AuthClientContext = createContext<AuthClientContextValue>(null)

export interface AuthClientProviderProps {
  client: AuthClient
  children: React.ReactNode
}

/**
 * Provider component that makes the auth client available to all child components.
 * Each app should wrap their root with this provider and pass their own auth client instance.
 *
 * @example
 * ```tsx
 * // In your app's layout or root component
 * import { AuthClientProvider } from '@ui/shared/auth/context'
 * import { authClient } from '~/auth/client'
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <AuthClientProvider client={authClient}>
 *       {children}
 *     </AuthClientProvider>
 *   )
 * }
 * ```
 */
export function AuthClientProvider({ client, children }: AuthClientProviderProps) {
  return <AuthClientContext.Provider value={client}>{children}</AuthClientContext.Provider>
}

/**
 * Hook to access the auth client instance from context.
 * Must be used within an AuthClientProvider.
 *
 * @throws Error if used outside of AuthClientProvider
 */
export function useAuthClient(): AuthClient {
  const client = useContext(AuthClientContext)
  if (!client) {
    throw new Error(
      'useAuthClient must be used within an AuthClientProvider. Make sure to wrap your app with <AuthClientProvider client={authClient}>.',
    )
  }
  return client
}
